import { z } from 'zod';

import { pagedSchema } from '@/shared/reference/wire';

// Contratos do fio da aba Operadores (U02). O item sai do tipo do legado
// (`services/accounts/bankAccount/types/operatorReadModelList.ts:3-27`), não do que a tabela lê:
// `OperatorReadModelList extends DefaultRecordDetails` (`contexts/apiRequestContext.tsx:235-245`).
// O tipo não marca nada como opcional, mas expressa ausência com `any`/null na prática: aqui tudo
// que a tela exibe entra `nullish`, e `looseObject` deixa passar o resto do `DefaultRecordDetails`.

const text = z.string().nullish();

/** `User` (`operatorReadModelList.ts:21-27`): o operador por trás da linha. */
export const operatorUserSchema = z.looseObject({
  userId: text,
  registrationNumber: text,
  userIdDisplay: text,
  tenantDisplay: text,
  tenant: text,
});
export type OperatorUser = z.infer<typeof operatorUserSchema>;

/** `OperatorReadModelList` (`operatorReadModelList.ts:3-19`). `approvals` alimenta o drawer de detalhes. */
export const operatorSchema = z.looseObject({
  id: z.string(),
  user: operatorUserSchema.nullish(),
  userId: text,
  level: text,
  levelDisplay: text,
  status: text,
  statusDisplay: text,
  isDeleted: z.boolean().nullish(),
  authorizedAt: text,
  bankAccountId: text,
  accountNumber: z.unknown().nullish(),
  ownerNameAccount: z.unknown().nullish(),
  ownerAccountType: z.unknown().nullish(),
  bankAccountRequestId: z.unknown().nullish(),
  registrationNumber: z.unknown().nullish(),
  approvals: z.array(z.looseObject({})).nullish(),
  tenantDisplay: text,
  createdAt: text,
  updatedAt: text,
});
export type Operator = z.infer<typeof operatorSchema>;

/**
 * `GET /BankAccount/{id}/Operator` → `GetListApiResponseSuccess<OperatorReadModelList>`
 * (`bankAccount.ts:338-341`, envelope em `apiRequestContext.tsx:110-117`): `data` é array direto.
 */
export const operatorPageSchema = pagedSchema(operatorSchema).extend({ type: text });
export type OperatorPage = z.infer<typeof operatorPageSchema>;

/** Níveis do seletor do formulário (`BankAccountOperatorForm.tsx:39-44`), na ordem do legado. */
/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio */
export const OPERATOR_LEVELS = ['Viewer', 'JointApprover', 'MasterApprover', 'Requester'] as const;
/** O filtro "Nível" não oferece `Requester` (`BankAccountFilters.tsx:115-119`). */
export const OPERATOR_FILTER_LEVELS = ['Viewer', 'JointApprover', 'MasterApprover'] as const;
/** Status do "Editar" (`EditOperatorForm/EditOperatorForm.tsx:53-56`): só estes dois. */
export const OPERATOR_EDIT_STATUSES = ['Approved', 'Timeout'] as const;
/* eslint-enable lingui/no-unlocalized-strings */
export type OperatorLevel = (typeof OPERATOR_LEVELS)[number];

/** `ParamsCreateOperator` (`types/generics.ts:50-54`): o corpo declarado do `POST`. */
export interface CreateOperatorBody {
  readonly level: string;
  readonly userId: string;
  readonly tenantName: string;
}

/** `UpdteBankAccountOperatorType` (`types/generics.ts:428-433`), aparado ao que o service envia (`bankAccount.ts:388-398`). */
export interface UpdateOperatorBody {
  readonly level: string;
  /** Só a conta digital no tenant raiz mostra o campo (`EditOperatorForm.tsx:27`); sem ele vai `null`. */
  readonly status: string | null;
}

/** `PayloadApproveOrRejectType` do recurso Operator (`types/generics.ts:61-69`), na ordem do legado. */
export interface OperatorApprovalBody {
  readonly action: 'Approve' | 'Reject';
  readonly code: string;
  readonly sessionId: string;
  readonly operatorIds: readonly string[];
}
